import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@mlm/ui';
import {
  COACHING_FOCUS_KEYS,
  COACHING_FOCUS_LABELS,
  COACHING_PLAN_PERIODS,
  COACHING_PLAN_TIERS,
  COACHING_READING_KIND_LABELS,
  COACHING_READING_KINDS,
  COACHING_TIERS,
  COACHING_TIER_LABELS,
  type CoachingReadingKind,
} from '@mlm/shared';
import { MemberPicker } from '../components/member-picker';
import { AudioEditor, BreathworkEditor } from './coaching-tool-editors';
import { hasRole, useHasPerm } from '../lib/auth-store';
import {
  createCoachingContent,
  deleteCoachingContent,
  getCoachingPlans,
  getCoachingReadingCopy,
  getCoachingUsage,
  getMemberCoachingEntitlement,
  grantMemberCoachingEntitlement,
  listCoachingContent,
  listProducts,
  patchCoachingContent,
  putCoachingPlans,
  revokeMemberCoachingEntitlement,
  type AdminUserSearchResult,
  type CoachingContentKind,
  type CoachingContentRow,
  type CoachingPlanEdit,
  type CoachingPlanPeriod,
  type CoachingPlanRow,
  type CoachingPlanTier,
  type CoachingTier,
} from '../lib/api';

/**
 * Admin → Coaching (Miracles Protocol): what the tenant SELLS, the copy behind
 * the chart and the daily loop, one member's plan, and what the module costs.
 *
 * The master switch stays in Settings → System, like the LMS one: this whole
 * page is hidden while coaching is off, so the control that turns it on cannot
 * live inside it. The per-tenant knobs (message caps, goal limits, the astrology
 * engine) stay on the Settings → Coaching card for the same reason — they are
 * settings, and this page is the module's day-to-day work.
 */
const TAB_VALUES = ['plans', 'content', 'members', 'usage'];

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';

export function CoachingPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab = TAB_VALUES.includes(raw ?? '') ? raw! : 'plans';
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Coaching</h1>
        <p className="text-sm text-muted-foreground">
          The coach app: the plans you sell, the copy members read, and who is on Pro.
        </p>
      </div>
      <Tabs
        value={tab}
        onValueChange={(v) => setParams(v === 'plans' ? {} : { tab: v }, { replace: true })}
      >
        <TabsList>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
        </TabsList>
        <TabsContent value="plans">
          <PlansTab />
        </TabsContent>
        <TabsContent value="content">
          <ContentTab />
        </TabsContent>
        <TabsContent value="members">
          <MembersTab />
        </TabsContent>
        <TabsContent value="usage">
          <UsageTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Plans ──────────────────────────────────────────────────────────────────

function PlansTab() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('tenant.features.write') || hasRole('super_admin');
  const plansQuery = useQuery({ queryKey: ['coaching-plans'], queryFn: getCoachingPlans });
  // Only APPROVED virtual products can back a plan, which is what the server
  // checks too — offering the rest would be a picker full of rejections.
  const productsQuery = useQuery({
    queryKey: ['coaching-plan-products'],
    queryFn: () => listProducts({ virtual: 'only', activeOnly: true, pageSize: 200 }),
    enabled: canWrite,
  });

  const save = useMutation({
    meta: { successMessage: 'Plans saved.' },
    mutationFn: (edits: CoachingPlanEdit[]) => putCoachingPlans(edits),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coaching-plans'] });
      qc.invalidateQueries({ queryKey: ['coaching-plan-products'] });
    },
  });

  const rows = plansQuery.data ?? [];
  const find = (tier: CoachingPlanTier, period: CoachingPlanPeriod) =>
    rows.find((p) => p.tier === tier && p.period === period) ?? null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Plans</CardTitle>
          <p className="text-sm text-muted-foreground">
            A plan is a hidden product: members buy it from the coach app&rsquo;s Upgrade page and
            it never appears in the store. Set the price here when you create it, and afterwards on
            the product itself under Virtual Products. Success Elite has no screen in the app yet,
            so it is sold only by a hand grant on the Members tab.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {plansQuery.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {COACHING_PLAN_TIERS.map((tier) => (
            <div key={tier} className="space-y-3">
              <h3 className="text-sm font-medium">{COACHING_TIER_LABELS[tier]}</h3>
              <div className="grid gap-3 md:grid-cols-2">
                {COACHING_PLAN_PERIODS.map((period) => (
                  <PlanCell
                    key={period}
                    tier={tier}
                    period={period}
                    row={find(tier, period)}
                    canWrite={canWrite}
                    products={(productsQuery.data ?? []).filter(
                      (p) => p.approvalStatus === 'approved',
                    )}
                    saving={save.isPending}
                    onSave={(edit) => save.mutate([edit])}
                  />
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function PlanCell({
  tier,
  period,
  row,
  canWrite,
  products,
  saving,
  onSave,
}: {
  tier: CoachingPlanTier;
  period: CoachingPlanPeriod;
  row: CoachingPlanRow | null;
  canWrite: boolean;
  products: Array<{ id: string; name: string; sku: string; price: string }>;
  saving: boolean;
  onSave: (edit: CoachingPlanEdit) => void;
}) {
  const [mode, setMode] = useState<'create' | 'existing'>('create');
  const [price, setPrice] = useState('');
  const [productId, setProductId] = useState('');

  const label = period === 'monthly' ? 'Monthly' : 'Annual';
  return (
    <div className="rounded-md border p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        {row ? (
          <Badge variant={row.active ? 'default' : 'secondary'}>
            {row.active ? 'On sale' : 'Withdrawn'}
          </Badge>
        ) : (
          <Badge variant="secondary">Not set up</Badge>
        )}
      </div>

      {row ? (
        <div className="space-y-2 text-sm">
          <div>
            <div>{row.productName}</div>
            <p className="text-xs text-muted-foreground">
              {row.sku} · ₱{row.price}
              {row.approvalStatus !== 'approved' && ` · product ${row.approvalStatus}`}
              {!row.productActive && ' · product inactive'}
            </p>
          </div>
          {canWrite && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => onSave({ tier, period, active: !row.active })}
              >
                {row.active ? 'Withdraw from sale' : 'Put on sale'}
              </Button>
              <Link
                to="/virtual-products"
                className="text-xs text-muted-foreground underline underline-offset-2"
              >
                Edit the price
              </Link>
            </div>
          )}
        </div>
      ) : canWrite ? (
        <div className="space-y-2">
          <Select value={mode} onValueChange={(v) => setMode(v as 'create' | 'existing')}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="create">Create a hidden product</SelectItem>
              <SelectItem value="existing">Use an existing virtual product</SelectItem>
            </SelectContent>
          </Select>
          {mode === 'create' ? (
            <Input
              className="h-8"
              placeholder="Price, e.g. 369"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          ) : (
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Choose a product" />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} (₱{p.price})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            size="sm"
            disabled={saving || (mode === 'create' ? !price.trim() : !productId)}
            onClick={() =>
              onSave(
                mode === 'create'
                  ? { tier, period, price: price.trim(), active: true }
                  : { tier, period, productId, active: true },
              )
            }
          >
            Set up {label.toLowerCase()}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Not set up.</p>
      )}
    </div>
  );
}

// ── Content ────────────────────────────────────────────────────────────────

const CONTENT_TABS: Array<{ kind: CoachingContentKind; label: string; blurb: string }> = [
  {
    kind: 'affirmation',
    label: 'Affirmations',
    blurb:
      'Added to the built-in set members draw on. Leave the focus blank to offer it to everyone.',
  },
  {
    kind: 'prompt',
    label: 'Journal prompts',
    blurb: 'Added to the built-in prompts, one of which leads each day.',
  },
  {
    kind: 'reading_copy',
    label: 'Chart reading copy',
    blurb:
      'Replaces one line of the built-in chart reading. Removing your version restores the original.',
  },
  {
    kind: 'breathwork',
    label: 'Breathwork',
    blurb:
      'The guided breathing patterns in Align. Edit a built-in pattern to change or hide it for your members, or add patterns of your own.',
  },
  {
    kind: 'audio',
    label: 'Frequencies',
    blurb:
      'The tracks members play in Align → Frequencies. Upload MP3 or M4A files up to 40 MB each.',
  },
];

function ContentTab() {
  const [kind, setKind] = useState<CoachingContentKind>('affirmation');
  const meta = CONTENT_TABS.find((t) => t.kind === kind)!;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Content</CardTitle>
        <p className="text-sm text-muted-foreground">{meta.blurb}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {CONTENT_TABS.map((t) => (
            <Button
              key={t.kind}
              size="sm"
              variant={t.kind === kind ? 'default' : 'outline'}
              onClick={() => setKind(t.kind)}
            >
              {t.label}
            </Button>
          ))}
        </div>
        {kind === 'reading_copy' ? (
          <ReadingCopyEditor />
        ) : kind === 'breathwork' ? (
          <BreathworkEditor />
        ) : kind === 'audio' ? (
          <AudioEditor />
        ) : (
          <LibraryEditor kind={kind} />
        )}
      </CardContent>
    </Card>
  );
}

function LibraryEditor({ kind }: { kind: 'affirmation' | 'prompt' }) {
  const qc = useQueryClient();
  const canWrite = useHasPerm('coaching.content.write') || hasRole('super_admin');
  const q = useQuery({
    queryKey: ['coaching-content', kind],
    queryFn: () => listCoachingContent(kind),
  });
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [premiumLevel, setPremiumLevel] = useState<CoachingTier>('free');
  const [removing, setRemoving] = useState<CoachingContentRow | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['coaching-content', kind] });
  const add = useMutation({
    meta: { successMessage: 'Added.' },
    mutationFn: () =>
      createCoachingContent(
        kind === 'affirmation'
          ? {
              kind,
              body: body.trim(),
              category: category === 'all' ? null : category,
              premiumLevel,
            }
          : { kind, body: body.trim(), premiumLevel },
      ),
    onSuccess: () => {
      setBody('');
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (row: CoachingContentRow) => patchCoachingContent(row.id, { active: !row.active }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    meta: { successMessage: 'Removed.' },
    mutationFn: (id: string) => deleteCoachingContent(id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          <Textarea
            rows={2}
            placeholder={
              kind === 'affirmation'
                ? 'I follow through on what I start.'
                : 'What would make today feel like a success?'
            }
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="flex flex-wrap items-end gap-3">
            {kind === 'affirmation' && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Focus</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-8 w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Everyone</SelectItem>
                    {COACHING_FOCUS_KEYS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {COACHING_FOCUS_LABELS[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Who sees it</label>
              <Select
                value={premiumLevel}
                onValueChange={(v) => setPremiumLevel(v as CoachingTier)}
              >
                <SelectTrigger className="h-8 w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COACHING_TIERS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t === 'free' ? 'Everyone' : `${COACHING_TIER_LABELS[t]} and above`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" disabled={!body.trim() || add.isPending} onClick={() => add.mutate()}>
              Add
            </Button>
          </div>
        </div>
      )}

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {q.data?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Nothing added yet. The built-in set is in use.
        </p>
      )}
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove this line?"
        description="Members stop seeing it. The built-in set is unaffected."
        confirmLabel="Remove"
        confirmVariant="destructive"
        loading={remove.isPending}
        onConfirm={async () => {
          await remove.mutateAsync(removing!.id);
          setRemoving(null);
        }}
      />
      <div className="divide-y rounded-md border">
        {(q.data ?? []).map((row) => (
          <div key={row.id} className="flex items-start justify-between gap-3 p-3 text-sm">
            <div className="min-w-0 space-y-1">
              <p className={row.active ? '' : 'text-muted-foreground line-through'}>{row.body}</p>
              <p className="text-xs text-muted-foreground">
                {row.premiumLevel === 'free'
                  ? 'Everyone'
                  : `${COACHING_TIER_LABELS[row.premiumLevel]} and above`}
                {row.category &&
                  ` · ${COACHING_FOCUS_LABELS[row.category as keyof typeof COACHING_FOCUS_LABELS] ?? row.category}`}
              </p>
            </div>
            {canWrite && (
              <div className="flex shrink-0 items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => toggle.mutate(row)}>
                  {row.active ? 'Hide' : 'Show'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRemoving(row)}>
                  Remove
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReadingCopyEditor() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('coaching.content.write') || hasRole('super_admin');
  const q = useQuery({ queryKey: ['coaching-reading-copy'], queryFn: getCoachingReadingCopy });
  const [readingKind, setReadingKind] = useState<CoachingReadingKind>('lagna');
  const [name, setName] = useState('');
  const [body, setBody] = useState('');

  const entries = q.data?.[readingKind] ?? [];
  const entry = entries.find((e) => e.name === name) ?? null;
  // Show what the member reads today: their override if there is one, else the
  // built-in line, so the editor is never a blank box over live copy.
  useEffect(() => {
    setBody(entry ? (entry.override?.body ?? entry.builtIn) : '');
  }, [entry]);
  useEffect(() => {
    setName(entries[0]?.name ?? '');
  }, [readingKind, q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => qc.invalidateQueries({ queryKey: ['coaching-reading-copy'] });
  const save = useMutation({
    meta: { successMessage: 'Saved.' },
    mutationFn: () => createCoachingContent({ kind: 'reading_copy', readingKind, name, body }),
    onSuccess: invalidate,
  });
  const reset = useMutation({
    meta: { successMessage: 'Restored the built-in wording.' },
    mutationFn: (id: string) => deleteCoachingContent(id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Part of the reading</label>
          <Select
            value={readingKind}
            onValueChange={(v) => setReadingKind(v as CoachingReadingKind)}
          >
            <SelectTrigger className="h-8 w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COACHING_READING_KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {COACHING_READING_KIND_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Entry</label>
          <Select value={name} onValueChange={setName}>
            <SelectTrigger className="h-8 w-56">
              <SelectValue placeholder="Choose one" />
            </SelectTrigger>
            <SelectContent>
              {entries.map((e) => (
                <SelectItem key={e.name} value={e.name}>
                  {e.name}
                  {e.override ? ' (edited)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Textarea
        rows={5}
        value={body}
        disabled={!canWrite || !name}
        onChange={(e) => setBody(e.target.value)}
      />
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!name || !body.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Save this wording
          </Button>
          {entry?.override && (
            <Button
              size="sm"
              variant="outline"
              disabled={reset.isPending}
              onClick={() => reset.mutate(entry.override!.id)}
            >
              Restore the built-in wording
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Members ────────────────────────────────────────────────────────────────

function MembersTab() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('user.write') || hasRole('super_admin');
  const [member, setMember] = useState<AdminUserSearchResult | null>(null);
  const [tier, setTier] = useState<CoachingPlanTier>('pro');
  const [days, setDays] = useState('30');
  const [note, setNote] = useState('');
  const [revoking, setRevoking] = useState(false);

  const q = useQuery({
    queryKey: ['coaching-entitlement', member?.id],
    queryFn: () => getMemberCoachingEntitlement(member!.id),
    enabled: !!member,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['coaching-entitlement', member?.id] });

  const grant = useMutation({
    meta: { successMessage: 'Plan granted.' },
    mutationFn: () =>
      grantMemberCoachingEntitlement(member!.id, {
        tier,
        addDays: Number(days),
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      setNote('');
      invalidate();
    },
  });
  const revoke = useMutation({
    meta: { successMessage: 'Plan revoked.' },
    mutationFn: () =>
      revokeMemberCoachingEntitlement(member!.id, { note: note.trim() || undefined }),
    onSuccess: invalidate,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
        <p className="text-sm text-muted-foreground">
          A grant opens access and charges nothing: no order, no wallet movement. Extra days are
          added to whatever the member already has in that plan, so extending someone can never
          shorten them.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <MemberPicker selected={member} onChange={setMember} />
        {member && q.data && (
          <>
            <div className="rounded-md border p-3 text-sm">
              <p className="font-medium">
                {q.data.tier === 'free' ? 'Free plan' : COACHING_TIER_LABELS[q.data.tier]}
              </p>
              <p className="text-xs text-muted-foreground">
                {q.data.tier === 'free'
                  ? 'No active plan.'
                  : `Until ${fmtDate(q.data.endsAt)}${q.data.source === 'admin_grant' ? ', granted by an admin' : ''}`}
              </p>
            </div>

            {canWrite && (
              <div className="flex flex-wrap items-end gap-3 rounded-md border bg-muted/20 p-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Plan</label>
                  <Select value={tier} onValueChange={(v) => setTier(v as CoachingPlanTier)}>
                    <SelectTrigger className="h-8 w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COACHING_PLAN_TIERS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {COACHING_TIER_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Days</label>
                  <Input
                    type="number"
                    min={1}
                    className="h-8 w-24"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                  />
                </div>
                <div className="space-y-1 grow">
                  <label className="text-xs text-muted-foreground">Note (optional)</label>
                  <Input
                    className="h-8"
                    placeholder="Why this was granted"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                <Button
                  size="sm"
                  disabled={grant.isPending || !(Number(days) > 0)}
                  onClick={() => grant.mutate()}
                >
                  Grant
                </Button>
                {q.data.tier !== 'free' && (
                  <Button size="sm" variant="outline" onClick={() => setRevoking(true)}>
                    Revoke
                  </Button>
                )}
              </div>
            )}

            <ConfirmDialog
              open={revoking}
              onOpenChange={setRevoking}
              title="Revoke this member's plan?"
              description="Every active window closes, including anything they paid for. What they have already written is kept."
              confirmLabel="Revoke"
              confirmVariant="destructive"
              loading={revoke.isPending}
              onConfirm={async () => {
                await revoke.mutateAsync();
                setRevoking(false);
              }}
            />

            <div className="divide-y rounded-md border text-sm">
              {q.data.rows.length === 0 && (
                <p className="p-3 text-muted-foreground">No plan history.</p>
              )}
              {q.data.rows.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                  <div>
                    <p>
                      {COACHING_TIER_LABELS[r.tier]} · {fmtDate(r.startsAt)} to {fmtDate(r.endsAt)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {r.source === 'purchase'
                        ? `Bought${r.orderCode ? ` on ${r.orderCode}` : ''}`
                        : `Granted${r.grantedByName ? ` by ${r.grantedByName}` : ''}`}
                      {r.note && ` · ${r.note}`}
                    </p>
                  </div>
                  {r.revokedAt && <Badge variant="secondary">Revoked</Badge>}
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Usage ──────────────────────────────────────────────────────────────────

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function UsageTab() {
  const q = useQuery({ queryKey: ['coaching-usage'], queryFn: getCoachingUsage });
  if (q.isLoading || !q.data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  const { astro, claude, engine, members } = q.data;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>This month</CardTitle>
          <p className="text-sm text-muted-foreground">
            Counted in your own timezone, from the first of the month.
          </p>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label={`Astrology calls (${astro.provider})`}
            value={String(astro.calls)}
            hint={
              astro.usedPct === null
                ? `${astro.okCalls} succeeded, ${astro.errorCalls} failed`
                : `${astro.usedPct}% of ${astro.monthlyCredits} credits${astro.alert ? ` · past your ${astro.alertPct}% alert` : ''}`
            }
          />
          <Stat
            label="AI spend"
            value={`$${Number(claude.costUsd).toFixed(2)}`}
            hint={`${claude.calls} calls, ${claude.kalaExchanges} with Master Kala${
              claude.budgetUsd ? ` · budget $${Number(claude.budgetUsd).toFixed(2)}` : ''
            }`}
          />
          <Stat
            label="Plans written today"
            value={String(engine.callsToday)}
            hint={`$${Number(engine.costTodayUsd).toFixed(2)} today`}
          />
          <Stat
            label="Members on a plan"
            value={String(members.pro + members.elite)}
            hint={`${members.pro} Pro, ${members.elite} Elite, ${members.onboarded} onboarded`}
          />
        </CardContent>
      </Card>
      {astro.alert && (
        <p className="text-sm text-destructive">
          Astrology credits are past your alert level. Charts fall back to the built-in engine when
          the pack runs out, so members keep their readings either way.
        </p>
      )}
    </div>
  );
}
